import React, { useEffect, useMemo, useRef } from 'react';
import { Alert, Spin } from 'antd';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import CertificateOfConformityPrintView from '@/components/quality/CertificateOfConformityPrintView';
import { certificateOfConformityService, type CertificateOfConformityRecord } from '@/services/certificate-of-conformity.service';
import { useSystemSettings } from '@/hooks/useSettings';

const CertificateOfConformityPrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: settings = [] } = useSystemSettings();
  const didTriggerPrint = useRef(false);

  const { data: record, isLoading, isError, error } = useQuery({
    queryKey: ['certificate-of-conformity-print', id],
    queryFn: async () => {
      if (!id) throw new Error('Missing id');
      const response = await certificateOfConformityService.getById(id);
      const raw = response.data?.data ?? response.data;
      return raw as CertificateOfConformityRecord;
    },
    enabled: !!id,
  });

  const companySettings = useMemo(
    () => settings.filter((setting) => setting.category === 'company'),
    [settings]
  );

  const companyInfo = useMemo(() => {
    const getSetting = (key: string) =>
      companySettings.find((setting) => setting.setting_key === key)?.setting_value?.trim() ?? '';

    const name = getSetting('company_name') || 'Company Name';
    const legalName = getSetting('company_legal_name') || name;
    const addressParts = [
      getSetting('company_address'),
      getSetting('company_address_line1'),
      getSetting('company_city'),
      getSetting('company_emirate_code'),
      getSetting('company_country_code'),
    ].filter(Boolean);

    return {
      name,
      legalName,
      address: addressParts.join(', ') || 'Company Address',
      logo: getSetting('company_logo') || undefined,
    };
  }, [companySettings]);

  const back = () => {
    navigate('/qms/certificates-of-conformity', { replace: true });
  };

  useEffect(() => {
    if (!record || didTriggerPrint.current) return;

    didTriggerPrint.current = true;

    const handleAfterPrint = () => {
      window.removeEventListener('afterprint', handleAfterPrint);
      back();
    };

    window.addEventListener('afterprint', handleAfterPrint);
    const timeoutId = window.setTimeout(() => {
      window.print();
    }, 350);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [record]);

  if (!id) {
    return <Navigate to="/qms/certificates-of-conformity" replace />;
  }

  if (isLoading) {
    return (
      <PrintPreviewChrome title="Certificate of Conformity - print preview" onBack={back}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 120 }}>
          <Spin size="large" />
        </div>
      </PrintPreviewChrome>
    );
  }

  if (isError || !record) {
    return (
      <PrintPreviewChrome title="Certificate of Conformity - print preview" onBack={back}>
        <div style={{ padding: 48 }}>
          <Alert
            type="error"
            showIcon
            message="Could not load Certificate of Conformity"
            description={(error as Error)?.message || 'Check the record and your permissions.'}
          />
        </div>
      </PrintPreviewChrome>
    );
  }

  return (
    <PrintPreviewChrome title="Certificate of Conformity - print preview" onBack={back}>
      <div className="print-doc-a4">
        <CertificateOfConformityPrintView record={record} companyInfo={companyInfo} />
      </div>
    </PrintPreviewChrome>
  );
};

export default CertificateOfConformityPrintPage;
